import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSwitchLanguage } from '../routing';
import { Sparkles, LogOut, Menu, X, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { rememberLocale } from '../utils/locale';
import { LANGUAGES, nextLanguage } from '../i18n';
import theme from '../theme';

const NAV_KEYS = [
  { to: '/admin',           key: 'dashboard' },
  { to: '/admin/products',  key: 'products' },
  { to: '/admin/rentals',   key: 'rentals' },
  // Directly under Rentals: a report is about a rental, and an unread one is
  // someone waiting at a locker.
  { to: '/admin/issues',    key: 'issues' },
  { to: '/admin/lockers',   key: 'lockers' },
  { to: '/admin/discounts', key: 'discounts' },
  { to: '/admin/customers', key: 'customers' },
  // Both are about money going OUT to other businesses rather than coming in
  // from customers, so they sit together and below the customer-facing screens.
  { to: '/admin/affiliates', key: 'affiliates' },
  { to: '/admin/licenses',  key: 'licenses' },
  // Public content we are answerable for, next to the people who wrote it.
  { to: '/admin/reviews',   key: 'reviews' },
  { to: '/admin/subscribers', key: 'subscribers' },
  { to: '/admin/documents', key: 'documents' },
  { to: '/admin/activity',  key: 'activity' },
  { to: '/browse',          key: 'viewStore' },
];

export default function AdminSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close sidebar on route change
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const handleLogout = () => { logout(); navigate('/'); };

  // The admin panel is fully translated but had no way to switch language: the
  // toggle lives in Navbar, and no admin screen renders Navbar. An operator who
  // signed in and went straight to /admin was stuck in whatever language the
  // browser happened to start in, and the only way out was to leave for the
  // public site and come back. Same as Navbar's toggle, including rememberLocale
  // — an admin is a user too, and their own mail should follow.
  // Navigates rather than only swapping the strings: with one URL per language
  // (/el/…, /en/…) leaving the path untouched would put English text under a
  // URL that still said /el — wrong for the reader who shares the link, and a
  // contradiction for the crawler that was told those are two pages.
  const switchLanguage = useSwitchLanguage();

  const toggleLang = () => {
    const next = nextLanguage(i18n.language);
    switchLanguage(next);
    rememberLocale(next);
  };

  return (
    <>
      <style>{`
        .admin-sidebar {
          width: 240px;
          background: ${theme.espresso};
          color: ${theme.white};
          padding: 32px 0;
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          bottom: 0;
          left: 0;
          z-index: 100;
        }
        .admin-main {
          margin-left: 240px;
          flex: 1;
        }
        .admin-mobile-bar {
          display: none;
        }
        @media (max-width: 768px) {
          .admin-sidebar {
            transform: translateX(-100%);
            transition: transform 0.25s ease;
          }
          .admin-sidebar.open {
            transform: translateX(0);
          }
          .admin-overlay {
            display: block;
            position: fixed;
            inset: 0;
            background: ${theme.alpha.black(0.5)};
            z-index: 99;
          }
          .admin-main {
            margin-left: 0 !important;
            padding-top: 60px !important;
          }
          .admin-mobile-bar {
            display: flex;
            align-items: center;
            gap: 12px;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            height: 56px;
            background: ${theme.espresso};
            padding: 0 16px;
            z-index: 98;
          }
        }
      `}</style>

      {/* Mobile top bar */}
      <div className="admin-mobile-bar">
        <button
          onClick={() => setMobileOpen(o => !o)}
          style={{ background: 'none', border: 'none', color: theme.white, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <Menu size={22} />
        </button>
        <span style={{ fontFamily: theme.font.display, fontSize: 20, color: theme.white }}>
          GLAM<span style={{ color: theme.gold }}>GO</span>
        </span>
      </div>

      {/* Overlay on mobile */}
      {mobileOpen && <div className="admin-overlay" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <div className={`admin-sidebar${mobileOpen ? ' open' : ''}`}>
        <div style={{ padding: '0 24px 32px', borderBottom: `1px solid ${theme.alpha.white(0.1)}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={16} color={theme.gold} />
              <span style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.white }}>
                GLAM<span style={{ color: theme.gold }}>GO</span>
              </span>
            </div>
            <button
              className="admin-close-btn"
              onClick={() => setMobileOpen(false)}
              style={{ background: 'none', border: 'none', color: theme.alpha.white(0.5), cursor: 'pointer', display: 'none', padding: 4 }}
            >
              <X size={18} />
            </button>
          </div>
          <div style={{ color: theme.alpha.white(0.5), fontSize: 11, marginTop: 4 }}>{t('adminNav.panel')}</div>
        </div>

        <nav style={{ padding: '24px 0', flex: 1 }}>
          {NAV_KEYS.map(item => {
            const active = pathname === item.to;
            return (
              <Link key={item.to} to={item.to} style={{
                display: 'block', padding: '12px 24px',
                color: active ? theme.gold : theme.alpha.white(0.75),
                background: active ? theme.alpha.gold(0.15) : 'transparent',
                textDecoration: 'none', fontSize: 14, letterSpacing: '0.05em',
                borderLeft: active ? `2px solid ${theme.gold}` : '2px solid transparent',
              }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = theme.alpha.gold(0.1); e.currentTarget.style.color = theme.gold; }}}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = theme.alpha.white(0.75); }}}
              >{t(`adminNav.${item.key}`)}</Link>
            );
          })}
        </nav>

        <div style={{ padding: '24px', borderTop: `1px solid ${theme.alpha.white(0.1)}` }}>
          <div style={{ color: theme.alpha.white(0.6), fontSize: 12, marginBottom: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.alpha.white(0.5), background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>
              <LogOut size={14} /> {t('adminNav.logout')}
            </button>
            <button
              onClick={toggleLang}
              aria-label={t('nav.switchLanguage')}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'none', border: `1px solid ${theme.alpha.white(0.2)}`,
                color: theme.alpha.white(0.7), cursor: 'pointer',
                fontSize: 11, letterSpacing: '0.08em', padding: '5px 10px', borderRadius: 2,
              }}
            >
              <Globe size={13} /> {LANGUAGES[nextLanguage(i18n.language)]?.short ?? 'EN'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
